import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Logout as LogoutIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import {
  loadPricingConfig,
  updateFinishingPricing,
  updateConnectorPricing,
  updateCableBasePrices,
  updatePowerWireGaugePricing,
  updateWyeCablePricing,
  updateLaborCosts,
  updateMarkups,
  savePricingConfig,
  bulkUpdatePricing,
  getRecentPricingChanges,
  subscribeToPricingUpdates,
  cleanupPricingUpdates
} from '../services/unifiedConfigService';
import { refreshFinishingPricingCache, refreshConnectorPricingCache, sendPricingNotification } from '../services/pricingService';
import { validatePricingConfig } from '../services/pricingValidationService';
import { hasPermission, logout, getCurrentUser } from '../services/authService';
import BulkPriceUpdate from './BulkPriceUpdate';
import Login from './Login';
import SyncStatus from './SyncStatus';
import { getCompatibilityMatrix } from '../services/compatibilitySystemV2';

function PricingAdmin() {
  // Pricing and admin state
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [historyDialog, setHistoryDialog] = useState(false);
  const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false);
  const [user, setUser] = useState(getCurrentUser());
  const [cablePriceInputs, setCablePriceInputs] = useState({});
  const [wireGaugePriceInputs, setWireGaugePriceInputs] = useState({});
  const [finishingPriceInputs, setFinishingPriceInputs] = useState({});
  const [realTimeUpdates, setRealTimeUpdates] = useState([]);
  const [recentChanges, setRecentChanges] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('notification');
  const [loading, setLoading] = useState(false);
  // Matrix-based connectors
  const [matrixConnectors, setMatrixConnectors] = useState([]);
  const [connectorEdits, setConnectorEdits] = useState({}); // { [id]: { ...edited fields... } }
  const [savingRows, setSavingRows] = useState({}); // { [id]: true/false }
  const [saveAllLoading, setSaveAllLoading] = useState(false);

  useEffect(() => {
    async function fetchConfig() {
      // Clear any existing cache before loading
      if (window.clearPricingCache) {
        window.clearPricingCache();
      }
      
      const loaded = await loadPricingConfig(true); // Admin mode - use RDS
      setConfig(loaded);
    }
    fetchConfig();
    // Real-time updates subscription
    const unsubscribe = subscribeToPricingUpdates((update) => {
      setRealTimeUpdates(prev => [update, ...prev.slice(0, 9)]);
    });
    // Recent changes
    getRecentPricingChanges(24).then(changes => {
      setRecentChanges(changes.changes || []);
    });
    return () => {
      unsubscribe();
      cleanupPricingUpdates();
    };
  }, []);

  // Initialize finishing price inputs when config loads
  useEffect(() => {
    if (config && config.finishingPricing) {
      setFinishingPriceInputs({
        heatshrinkWrap: config.finishingPricing.heatshrinkWrap,
        customHeatShrinkPerEnd: config.finishingPricing.customHeatShrinkPerEnd,
        codingRings: config.finishingPricing.codingRings
      });
    }
  }, [config]);

  useEffect(() => {
    async function fetchConnectorsWithPricing() {
      try {
        // Get connectors that have pricing from RDS
        const response = await fetch('/api/config-manager?action=connectors-with-pricing');
        if (response.ok) {
          const { connectorIds } = await response.json();
          console.log('DEBUG: Connectors with pricing from RDS:', connectorIds.length);
          
          // Instead of trying to match with compatibility matrix, create connector objects directly from pricing data
          const connectorsWithPricing = connectorIds.map(connectorId => ({
            id: connectorId,
            type: 'Unknown', // We'll get this from the pricing data if available
            description: `Connector ${connectorId}`,
            hasPricing: true
          }));
          
          console.log('DEBUG: Created connector objects from pricing data:', connectorsWithPricing.length);
          
          // Debug: Check what's in the config object
          if (config) {
            console.log('DEBUG: Config panelMountPricing count:', Object.keys(config.panelMountPricing || {}).length);
            console.log('DEBUG: Config inlinePricing count:', Object.keys(config.inlinePricing || {}).length);
            console.log('DEBUG: Sample panelMountPricing keys:', Object.keys(config.panelMountPricing || {}).slice(0, 5));
            console.log('DEBUG: Sample inlinePricing keys:', Object.keys(config.inlinePricing || {}).slice(0, 5));
          }
          
          setMatrixConnectors(connectorsWithPricing);
        } else {
          console.error('DEBUG: Failed to fetch connectors with pricing from RDS');
          setMatrixConnectors([]);
        }
      } catch (e) {
        console.error('DEBUG: Error loading connectors with pricing:', e);
        setMatrixConnectors([]);
      }
    }
    
    // Only fetch connectors after config is loaded
    if (config) {
      fetchConnectorsWithPricing();
    }
  }, [config]);

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);
    // Reload config for price-related tabs (0, 1, 2, 4)
    if ([0, 1, 2, 4].includes(newValue)) {
      const loaded = await loadPricingConfig(true); // Admin mode - use RDS
      setConfig(loaded);
    }
  };

  const handleCablePriceChange = (cableType, value) => {
    if (!hasPermission('manage_pricing')) {
      showSnackbar('You do not have permission to update prices', 'error');
      return;
    }
    const newPrice = parseFloat(value);
    if (!isNaN(newPrice)) {
      const updatedConfig = {
        ...config,
        cableBasePrices: {
          ...config.cableBasePrices,
          [cableType]: newPrice
        }
      };
      const validation = validatePricingConfig(updatedConfig);
      if (!validation.isValid) {
        showSnackbar(validation.errors[0], 'error');
        return;
      }
      setConfig(updatedConfig);
      updateCableBasePrices(cableType, newPrice).then(async () => {
        const loaded = await loadPricingConfig(true); // Admin mode - use RDS
        setConfig(loaded);
      });
      showSnackbar('Cable price updated successfully');
    }
  };

  const handleFinishingPriceChange = (value) => {
    if (!hasPermission('manage_pricing')) {
      showSnackbar('You do not have permission to update prices', 'error');
      return;
    }
    const newPrice = parseFloat(value);
    if (!isNaN(newPrice)) {
      const updatedConfig = {
        ...config,
        finishingPricing: {
          ...config.finishingPricing,
          heatshrinkWrap: newPrice
        }
      };
      const validation = validatePricingConfig(updatedConfig);
      if (!validation.isValid) {
        showSnackbar(validation.errors[0], 'error');
        return;
      }
      setConfig(updatedConfig);
      updateFinishingPricing('heatshrinkWrap', newPrice).then(async () => {
        const loaded = await loadPricingConfig(true); // Admin mode - use RDS
        setConfig(loaded);
        showSnackbar('Heatshrink wrap price updated successfully', 'success');
        // Refresh the pricing service cache so UI updates immediately
        refreshFinishingPricingCache();
      }).catch(error => {
        console.error('Failed to update heatshrink wrap price:', error);
        showSnackbar('Failed to update heatshrink wrap price', 'error');
      });
    }
  };

  const handleCustomHeatShrinkPerEndChange = (value) => {
    if (!hasPermission('manage_pricing')) {
      showSnackbar('You do not have permission to update prices', 'error');
      return;
    }
    const newPrice = parseFloat(value);
    if (!isNaN(newPrice)) {
      const updatedConfig = {
        ...config,
        finishingPricing: {
          ...config.finishingPricing,
          customHeatShrinkPerEnd: newPrice
        }
      };
      const validation = validatePricingConfig(updatedConfig);
      if (!validation.isValid) {
        showSnackbar(validation.errors[0], 'error');
        return;
      }
      setConfig(updatedConfig);
      updateFinishingPricing('customHeatShrinkPerEnd', newPrice).then(async () => {
        const loaded = await loadPricingConfig(true);
        setConfig(loaded);
        showSnackbar('Custom heat shrink price updated successfully', 'success');
        refreshFinishingPricingCache();
      }).catch(error => {
        console.error('Failed to update custom heat shrink price:', error);
        showSnackbar('Failed to update custom heat shrink price', 'error');
      });
    }
  };

  const handleCodingRingPriceChange = (value) => {
    if (!hasPermission('manage_pricing')) {
      showSnackbar('You do not have permission to update prices', 'error');
      return;
    }
    const newPrice = parseFloat(value);
    if (!isNaN(newPrice)) {
      const updatedConfig = {
        ...config,
        finishingPricing: {
          ...config.finishingPricing,
          codingRings: newPrice
        }
      };
      const validation = validatePricingConfig(updatedConfig);
      if (!validation.isValid) {
        showSnackbar(validation.errors[0], 'error');
        return;
      }
      setConfig(updatedConfig);
      updateFinishingPricing('codingRings', newPrice).then(async () => {
        const loaded = await loadPricingConfig(true); // Admin mode - use RDS
        setConfig(loaded);
        showSnackbar('Coding ring price updated successfully', 'success');
        // Refresh the pricing service cache so UI updates immediately
        refreshFinishingPricingCache();
      }).catch(error => {
        console.error('Failed to update coding ring price:', error);
        showSnackbar('Failed to update coding ring price', 'error');
      });
    }
  };

  const handleWireGaugePriceChange = (gauge, value) => {
    if (!hasPermission('manage_pricing')) {
      showSnackbar('You do not have permission to update prices', 'error');
      return;
    }
    const newPrice = parseFloat(value);
    if (!isNaN(newPrice)) {
      const updatedConfig = {
        ...config,
        powerWireGaugePricing: {
          ...config.powerWireGaugePricing,
          [gauge]: newPrice
        }
      };
      const validation = validatePricingConfig(updatedConfig);
      if (!validation.isValid) {
        showSnackbar(validation.errors[0], 'error');
        return;
      }
      setConfig(updatedConfig);
      updatePowerWireGaugePricing(gauge, newPrice).then(async () => {
        const loaded = await loadPricingConfig(true); // Admin mode - use RDS
        setConfig(loaded);
      });
      showSnackbar('Wire gauge price updated successfully');
    }
  };

  const handleWyeCablePriceChange = (cableType, value) => {
    if (!hasPermission('manage_pricing')) {
      showSnackbar('You do not have permission to update prices', 'error');
      return;
    }
    const newPrice = parseFloat(value);
    if (!isNaN(newPrice)) {
      const updatedConfig = {
        ...config,
        wyeCablePricing: {
          ...config.wyeCablePricing,
          [cableType]: newPrice
        }
      };
      const validation = validatePricingConfig(updatedConfig);
      if (!validation.isValid) {
        showSnackbar(validation.errors[0], 'error');
        return;
      }
      setConfig(updatedConfig);
      updateWyeCablePricing(cableType, newPrice).then(async () => {
        const loaded = await loadPricingConfig(true); // Admin mode - use RDS
        setConfig(loaded);
      });
      showSnackbar('Wye cable price updated successfully');
    }
  };



  const handleBulkUpdate = (update) => {
    if (!hasPermission('bulk_update')) {
      showSnackbar('You do not have permission to perform bulk updates', 'error');
      return;
    }
    const updatedConfig = { ...config };
    const section = update.type;
    if (update.method === 'percentage') {
      const multiplier = 1 + (update.value / 100);
      if (section === 'cableBasePrices') {
        Object.keys(updatedConfig.cableBasePrices || {}).forEach(type => {
          updatedConfig.cableBasePrices[type] *= multiplier;
        });
      } else if (section === 'powerWireGaugePricing') {
        Object.keys(updatedConfig.powerWireGaugePricing || {}).forEach(gauge => {
          updatedConfig.powerWireGaugePricing[gauge] *= multiplier;
        });
      } else if (section === 'wyeCablePricing') {
        Object.keys(updatedConfig.wyeCablePricing || {}).forEach(cableType => {
          updatedConfig.wyeCablePricing[cableType] *= multiplier;
        });
      } else if (section === 'panelMountPricing' || section === 'inlinePricing') {
        Object.keys(updatedConfig[section] || {}).forEach(connector => {
          updatedConfig[section][connector].partCost *= multiplier;
          updatedConfig[section][connector].laborCost *= multiplier;
        });
      } else if (section === 'finishingPricing') {
        Object.keys(updatedConfig.finishingPricing || {}).forEach(option => {
          Object.keys(updatedConfig.finishingPricing[option] || {}).forEach(type => {
            updatedConfig.finishingPricing[option][type] *= multiplier;
          });
        });
      } else if (section === 'laborCosts') {
        Object.keys(updatedConfig.laborCosts || {}).forEach(quality => {
          updatedConfig.laborCosts[quality] *= multiplier;
        });
      }
    } else if (update.method === 'fixed') {
      if (section === 'cableBasePrices') {
        Object.keys(updatedConfig.cableBasePrices || {}).forEach(type => {
          updatedConfig.cableBasePrices[type] += update.value;
        });
      } else if (section === 'powerWireGaugePricing') {
        Object.keys(updatedConfig.powerWireGaugePricing || {}).forEach(gauge => {
          updatedConfig.powerWireGaugePricing[gauge] += update.value;
        });
      } else if (section === 'wyeCablePricing') {
        Object.keys(updatedConfig.wyeCablePricing || {}).forEach(cableType => {
          updatedConfig.wyeCablePricing[cableType] += update.value;
        });
      } else if (section === 'panelMountPricing' || section === 'inlinePricing') {
        Object.keys(updatedConfig[section] || {}).forEach(connector => {
          updatedConfig[section][connector].partCost += update.value;
          updatedConfig[section][connector].laborCost += update.value;
        });
      } else if (section === 'finishingPricing') {
        Object.keys(updatedConfig.finishingPricing || {}).forEach(option => {
          Object.keys(updatedConfig.finishingPricing[option] || {}).forEach(type => {
            updatedConfig.finishingPricing[option][type] += update.value;
          });
        });
      } else if (section === 'laborCosts') {
        Object.keys(updatedConfig.laborCosts || {}).forEach(quality => {
          updatedConfig.laborCosts[quality] += update.value;
        });
      }
    }
    const validation = validatePricingConfig(updatedConfig);
    if (!validation.isValid) {
      showSnackbar(validation.errors[0], 'error');
      return;
    }
    setConfig(updatedConfig);
    savePricingConfig(updatedConfig);
    showSnackbar('Bulk update applied successfully');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogout = () => {
    logout();
    setUser(null);
  };

  const handleSendNotification = async () => {
    if (!notificationMessage.trim()) {
      showSnackbar('Please enter a notification message', 'error');
      return;
    }
    setLoading(true);
    try {
      await sendPricingNotification(notificationMessage, notificationType);
      showSnackbar('Notification sent successfully!');
      setNotificationMessage('');
    } catch (error) {
      showSnackbar('Error sending notification: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Compatibility/Connectors tab handlers
  if (!user) {
    return <Login onLoginSuccess={setUser} />;
  }

  if (!config || !config.cableBasePrices || !config.finishingPricing) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Loading pricing configuration...</Typography>
      </Box>
    );
  }

  const tabs = [
    'Cable Base Prices',
    'Power Wire Gauge Pricing',
    'Finishing Pricing',
    'Connectors',
    'Connector Pricing', // NEW TAB
    'Real-Time Updates',
    'Notifications',
    'Recent Changes'
  ];

  // Handler for editing connector pricing fields
  const handleConnectorEdit = (id, field, value) => {
    setConnectorEdits(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  // Handler for saving a single connector row
  const handleSaveConnector = async (id) => {
    setSavingRows(prev => ({ ...prev, [id]: true }));
    const edit = connectorEdits[id];
    
    try {
      // Update panel mount pricing if changed
      if (edit.panelPart !== undefined || edit.panelLabor !== undefined) {
        const panelPricing = {
          partCost: edit.panelPart !== undefined ? parseFloat(edit.panelPart) : config.panelMountPricing?.[id]?.partCost || 0,
          laborCost: edit.panelLabor !== undefined ? parseFloat(edit.panelLabor) : config.panelMountPricing?.[id]?.laborCost || 2.50
        };
        await updateConnectorPricing(id, panelPricing, true); // true = panel mount
      }
      
      // Update inline pricing if changed
      if (edit.inlinePart !== undefined || edit.inlineLabor !== undefined) {
        const inlinePricing = {
          partCost: edit.inlinePart !== undefined ? parseFloat(edit.inlinePart) : config.inlinePricing?.[id]?.partCost || 0,
          laborCost: edit.inlineLabor !== undefined ? parseFloat(edit.inlineLabor) : config.inlinePricing?.[id]?.laborCost || 2.50
        };
        await updateConnectorPricing(id, inlinePricing, false); // false = inline
      }
      
      // Reload config to get updated data
      const updatedConfig = await loadPricingConfig(true); // Admin mode - use RDS
      setConfig(updatedConfig);
      setConnectorEdits(prev => { const c = { ...prev }; delete c[id]; return c; });
      showSnackbar(`Connector ${id} pricing updated successfully`);
      refreshConnectorPricingCache(); // Refresh cache after successful save
    } catch (error) {
      console.error('Error saving connector pricing:', error);
      showSnackbar(`Error saving connector ${id}: ${error.message}`, 'error');
    } finally {
      setSavingRows(prev => ({ ...prev, [id]: false }));
    }
  };

  // Handler for saving all dirty rows
  const handleSaveAll = async () => {
    setSaveAllLoading(true);
    
    try {
      const updates = [];
      
      // Prepare all updates
      Object.entries(connectorEdits).forEach(([id, edit]) => {
        // Panel mount updates
        if (edit.panelPart !== undefined || edit.panelLabor !== undefined) {
          updates.push({
            connectorId: id,
            mountType: 'panel',
            partCost: edit.panelPart !== undefined ? parseFloat(edit.panelPart) : config.panelMountPricing?.[id]?.partCost || 0,
            laborCost: edit.panelLabor !== undefined ? parseFloat(edit.panelLabor) : config.panelMountPricing?.[id]?.laborCost || 2.50
          });
        }
        
        // Inline updates
        if (edit.inlinePart !== undefined || edit.inlineLabor !== undefined) {
          updates.push({
            connectorId: id,
            mountType: 'inline',
            partCost: edit.inlinePart !== undefined ? parseFloat(edit.inlinePart) : config.inlinePricing?.[id]?.partCost || 0,
            laborCost: edit.inlineLabor !== undefined ? parseFloat(edit.inlineLabor) : config.inlinePricing?.[id]?.laborCost || 2.50
          });
        }
      });
      
      // Perform bulk update
      await bulkUpdatePricing(updates);
      
      // Reload config to get updated data
      const updatedConfig = await loadPricingConfig(true); // Admin mode - use RDS
      setConfig(updatedConfig);
      setConnectorEdits({});
      showSnackbar(`${updates.length} connector pricing updates completed successfully`);
      refreshConnectorPricingCache(); // Refresh cache after successful save
    } catch (error) {
      console.error('Error saving all connector pricing:', error);
      showSnackbar(`Error saving connector pricing: ${error.message}`, 'error');
    } finally {
      setSaveAllLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#1976d2', color: '#fff', px: 2, py: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 0 }}>
          Pricing Administration
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          sx={{ ml: 2, bgcolor: '#fff', color: '#1976d2', fontWeight: 600, boxShadow: 1 }}
          href="/"
        >
          Back to Site
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="subtitle1" sx={{ mr: 2 }}>
          {user.name} ({user.role})
        </Typography>
        <IconButton color="inherit" onClick={handleLogout}>
          <LogoutIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 3 }}>
        <SyncStatus isAdmin={hasPermission('admin')} />
      </Box>

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, idx) => (
            <Tab key={tab} label={tab} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ p: 3 }}>
        {activeTab === 0 && (
          <Grid container spacing={2}>
            {/* Cable Base Prices */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Cable Base Prices
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    Object.entries(config.cableBasePrices || {}).forEach(([cableType, price]) => {
                      const inputValue = cablePriceInputs[cableType];
                      if (inputValue !== undefined) {
                        handleCablePriceChange(cableType, inputValue);
                      }
                    });
                  }}
                  disabled={!hasPermission('manage_pricing')}
                >
                  Save All Cable Prices
                </Button>
              </Box>
            </Grid>
            {Object.entries(config.cableBasePrices || {}).map(([cableType, price]) => (
              <Grid item xs={12} md={6} key={cableType}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {cableType.charAt(0).toUpperCase() + cableType.slice(1)}
                  </Typography>
                  <TextField
                    label={`${cableType.charAt(0).toUpperCase() + cableType.slice(1)} Price`}
                    type="number"
                    value={cablePriceInputs[cableType] !== undefined ? cablePriceInputs[cableType] : (price !== null && price !== undefined ? price.toFixed(2) : '0.00')}
                    onChange={(e) => setCablePriceInputs({ ...cablePriceInputs, [cableType]: e.target.value })}
                    onBlur={() => handleCablePriceChange(cableType, cablePriceInputs[cableType])}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleCablePriceChange(cableType, cablePriceInputs[cableType]); }}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: '$'
                    }}
                    disabled={!hasPermission('manage_pricing')}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleCablePriceChange(cableType, cablePriceInputs[cableType])}
                    disabled={!hasPermission('manage_pricing')}
                    sx={{ mt: 1, width: '100%' }}
                  >
                    Save {cableType.charAt(0).toUpperCase() + cableType.slice(1)} Price
                  </Button>
                </Paper>
              </Grid>
            ))}
            
            {/* Wye Cable Pricing */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Wye Cable Pricing
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    Object.entries(config.wyeCablePricing || {}).forEach(([cableType, price]) => {
                      const inputValue = cablePriceInputs[`wye-${cableType}`];
                      if (inputValue !== undefined) {
                        handleWyeCablePriceChange(cableType, inputValue);
                      }
                    });
                  }}
                  disabled={!hasPermission('manage_pricing')}
                >
                  Save All Wye Prices
                </Button>
              </Box>
            </Grid>
            {Object.entries(config.wyeCablePricing || {}).map(([cableType, price]) => (
              <Grid item xs={12} md={6} key={`wye-${cableType}`}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {cableType.charAt(0).toUpperCase() + cableType.slice(1)} Wye Cable Price
                  </Typography>
                  <TextField
                    label={`${cableType.charAt(0).toUpperCase() + cableType.slice(1)} Wye Cable Price (per foot)`}
                    type="number"
                    value={cablePriceInputs[`wye-${cableType}`] !== undefined ? cablePriceInputs[`wye-${cableType}`] : (price !== null && price !== undefined ? price.toFixed(2) : '0.00')}
                    onChange={(e) => setCablePriceInputs({ ...cablePriceInputs, [`wye-${cableType}`]: e.target.value })}
                    onBlur={() => handleWyeCablePriceChange(cableType, cablePriceInputs[`wye-${cableType}`])}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleWyeCablePriceChange(cableType, cablePriceInputs[`wye-${cableType}`]); }}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: '$'
                    }}
                    disabled={!hasPermission('manage_pricing')}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleWyeCablePriceChange(cableType, cablePriceInputs[`wye-${cableType}`])}
                    disabled={!hasPermission('manage_pricing')}
                    sx={{ mt: 1, width: '100%' }}
                  >
                    Save {cableType.charAt(0).toUpperCase() + cableType.slice(1)} Wye Price
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Power Wire Gauge Pricing
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    Object.entries(config.powerWireGaugePricing || {}).forEach(([gauge, price]) => {
                      const inputValue = wireGaugePriceInputs[gauge];
                      if (inputValue !== undefined) {
                        handleWireGaugePriceChange(gauge, inputValue);
                      }
                    });
                  }}
                  disabled={!hasPermission('manage_pricing')}
                >
                  Save All Power Prices
                </Button>
              </Box>
            </Grid>
            {Object.entries(config.powerWireGaugePricing || {}).map(([gauge, price]) => (
              <Grid item xs={12} md={6} key={gauge}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {gauge} Power Cable Price
                  </Typography>
                  <TextField
                    label={`${gauge} Power Cable Price (per foot)`}
                    type="number"
                    value={wireGaugePriceInputs[gauge] !== undefined ? wireGaugePriceInputs[gauge] : (price !== null && price !== undefined ? price.toFixed(2) : '0.00')}
                    onChange={(e) => setWireGaugePriceInputs({ ...wireGaugePriceInputs, [gauge]: e.target.value })}
                    onBlur={() => handleWireGaugePriceChange(gauge, wireGaugePriceInputs[gauge])}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleWireGaugePriceChange(gauge, wireGaugePriceInputs[gauge]); }}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: '$'
                    }}
                    disabled={!hasPermission('manage_pricing')}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleWireGaugePriceChange(gauge, wireGaugePriceInputs[gauge])}
                    disabled={!hasPermission('manage_pricing')}
                    sx={{ mt: 1, width: '100%' }}
                  >
                    Save {gauge} Power Price
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}



        {activeTab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  Finishing Pricing
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    // Save heatshrink price
                    if (finishingPriceInputs.heatshrinkWrap !== undefined && !isNaN(finishingPriceInputs.heatshrinkWrap)) {
                      handleFinishingPriceChange(finishingPriceInputs.heatshrinkWrap);
                    }
                    if (
                      finishingPriceInputs.customHeatShrinkPerEnd !== undefined &&
                      !isNaN(finishingPriceInputs.customHeatShrinkPerEnd)
                    ) {
                      handleCustomHeatShrinkPerEndChange(finishingPriceInputs.customHeatShrinkPerEnd);
                    }
                    // Save coding ring price
                    if (finishingPriceInputs.codingRings !== undefined && !isNaN(finishingPriceInputs.codingRings)) {
                      handleCodingRingPriceChange(finishingPriceInputs.codingRings);
                    }
                  }}
                  disabled={!hasPermission('manage_pricing')}
                >
                  Save All Finishing Prices
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  2&quot; clear heat shrink (per end) price
                </Typography>
                <TextField
                  label={'2" clear heat shrink (per end) price'}
                  type="number"
                  step="0.01"
                  min="0"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={finishingPriceInputs.heatshrinkWrap !== undefined ? finishingPriceInputs.heatshrinkWrap : (config.finishingPricing?.heatshrinkWrap || 0)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === null) {
                      setFinishingPriceInputs({ ...finishingPriceInputs, heatshrinkWrap: undefined });
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setFinishingPriceInputs({ ...finishingPriceInputs, heatshrinkWrap: numValue });
                      }
                    }
                  }}
                  onBlur={() => {
                    if (finishingPriceInputs.heatshrinkWrap !== undefined && !isNaN(finishingPriceInputs.heatshrinkWrap)) {
                      handleFinishingPriceChange(finishingPriceInputs.heatshrinkWrap);
                    }
                  }}
                  onKeyPress={(e) => { 
                    if (e.key === 'Enter' && finishingPriceInputs.heatshrinkWrap !== undefined && !isNaN(finishingPriceInputs.heatshrinkWrap)) {
                      handleFinishingPriceChange(finishingPriceInputs.heatshrinkWrap);
                    }
                  }}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: '$'
                  }}
                  disabled={!hasPermission('manage_pricing')}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (finishingPriceInputs.heatshrinkWrap !== undefined && !isNaN(finishingPriceInputs.heatshrinkWrap)) {
                      handleFinishingPriceChange(finishingPriceInputs.heatshrinkWrap);
                    }
                  }}
                  disabled={!hasPermission('manage_pricing')}
                  sx={{ mt: 1, width: '100%' }}
                >
                  Save clear heat-shrink price
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  2&quot; custom printed heat shrink (per end) price
                </Typography>
                <TextField
                  label={'2" custom printed heat shrink (per end) price'}
                  type="number"
                  step="0.01"
                  min="0"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={
                    finishingPriceInputs.customHeatShrinkPerEnd !== undefined
                      ? finishingPriceInputs.customHeatShrinkPerEnd
                      : config.finishingPricing?.customHeatShrinkPerEnd ?? 3
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === null) {
                      setFinishingPriceInputs({ ...finishingPriceInputs, customHeatShrinkPerEnd: undefined });
                    } else {
                      const numValue = parseFloat(val);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setFinishingPriceInputs({ ...finishingPriceInputs, customHeatShrinkPerEnd: numValue });
                      }
                    }
                  }}
                  onBlur={() => {
                    if (
                      finishingPriceInputs.customHeatShrinkPerEnd !== undefined &&
                      !isNaN(finishingPriceInputs.customHeatShrinkPerEnd)
                    ) {
                      handleCustomHeatShrinkPerEndChange(finishingPriceInputs.customHeatShrinkPerEnd);
                    }
                  }}
                  fullWidth
                  margin="normal"
                  InputProps={{ startAdornment: '$' }}
                  disabled={!hasPermission('manage_pricing')}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (
                      finishingPriceInputs.customHeatShrinkPerEnd !== undefined &&
                      !isNaN(finishingPriceInputs.customHeatShrinkPerEnd)
                    ) {
                      handleCustomHeatShrinkPerEndChange(finishingPriceInputs.customHeatShrinkPerEnd);
                    }
                  }}
                  disabled={!hasPermission('manage_pricing')}
                  sx={{ mt: 1, width: '100%' }}
                >
                  Save Custom Heat Shrink Price
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  XLR Coding Ring (per end) Price
                </Typography>
                <TextField
                  label={'XLR Coding Ring (per end) Price'}
                  type="number"
                  step="0.01"
                  min="0"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={finishingPriceInputs.codingRings !== undefined ? finishingPriceInputs.codingRings : (config.finishingPricing?.codingRings || 0)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === null) {
                      setFinishingPriceInputs({ ...finishingPriceInputs, codingRings: undefined });
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setFinishingPriceInputs({ ...finishingPriceInputs, codingRings: numValue });
                      }
                    }
                  }}
                  onBlur={() => {
                    if (finishingPriceInputs.codingRings !== undefined && !isNaN(finishingPriceInputs.codingRings)) {
                      handleCodingRingPriceChange(finishingPriceInputs.codingRings);
                    }
                  }}
                  onKeyPress={(e) => { 
                    if (e.key === 'Enter' && finishingPriceInputs.codingRings !== undefined && !isNaN(finishingPriceInputs.codingRings)) {
                      handleCodingRingPriceChange(finishingPriceInputs.codingRings);
                    }
                  }}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: '$'
                  }}
                  disabled={!hasPermission('manage_pricing')}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (finishingPriceInputs.codingRings !== undefined && !isNaN(finishingPriceInputs.codingRings)) {
                      handleCodingRingPriceChange(finishingPriceInputs.codingRings);
                    }
                  }}
                  disabled={!hasPermission('manage_pricing')}
                  sx={{ mt: 1, width: '100%' }}
                >
                  Save Coding Ring Price
                </Button>
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 3 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Connectors (from Compatibility Matrix)</Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Connector ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Verified</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrixConnectors.map(connector => (
                    <TableRow key={connector.id}>
                      <TableCell>{connector.id}</TableCell>
                      <TableCell>{connector.type}</TableCell>
                      <TableCell>{connector.brand}</TableCell>
                      <TableCell>{connector.gender}</TableCell>
                      <TableCell>{connector.description}</TableCell>
                      <TableCell>
                        {connector.verified ? (
                          <Chip label="Verified" color="success" size="small" />
                        ) : (
                          <Tooltip title="Compatibility not verified. Please confirm before ordering.">
                            <Chip
                              icon={<WarningAmberIcon fontSize="small" />}
                              label="Unverified"
                              color="warning"
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeTab === 4 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Connector Pricing ({matrixConnectors.length} connectors with pricing)
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '15%' }}>Connector ID</TableCell>
                    <TableCell sx={{ width: '25%' }}>Description</TableCell>
                    <TableCell sx={{ width: '18%' }}>Panel Mount Price</TableCell>
                    <TableCell sx={{ width: '18%' }}>Panel Mount Labor</TableCell>
                    <TableCell sx={{ width: '18%' }}>Inline Price</TableCell>
                    <TableCell sx={{ width: '18%' }}>Inline Labor</TableCell>
                    <TableCell sx={{ width: '8%' }}>Save</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrixConnectors.map(connector => {
                    const panel = config.panelMountPricing[connector.id];
                    const inline = config.inlinePricing[connector.id];
                    const edit = connectorEdits[connector.id] || {};
                    const isDirty = !!connectorEdits[connector.id];
                    
                    // Debug: Log what pricing data is found for this connector
                    if (connector.id === matrixConnectors[0]?.id) {
                      console.log('DEBUG: First connector pricing lookup:', {
                        connectorId: connector.id,
                        panel: panel,
                        inline: inline,
                        panelMountPricingKeys: Object.keys(config.panelMountPricing || {}),
                        inlinePricingKeys: Object.keys(config.inlinePricing || {})
                      });
                    }
                    return (
                      <TableRow key={connector.id}>
                        <TableCell sx={{ width: '15%' }}>{connector.id}</TableCell>
                        <TableCell sx={{ width: '25%' }}>{connector.description || ''}</TableCell>
                        <TableCell sx={{ width: '18%' }}>
                           <TextField
                             value={edit.panelPart !== undefined ? edit.panelPart : (panel ? panel.partCost : '')}
                             onChange={e => handleConnectorEdit(connector.id, 'panelPart', e.target.value)}
                             size="small"
                             type="number"
                             inputProps={{ 
                               min: 0, 
                               step: 0.01,
                               style: { 
                                 WebkitAppearance: 'none',
                                 MozAppearance: 'textfield'
                               }
                             }}
                             sx={{ 
                               width: '100%',
                               '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                 WebkitAppearance: 'none',
                                 margin: 0
                               },
                               '& input[type=number]': {
                                 MozAppearance: 'textfield'
                               }
                             }}
                           />
                         </TableCell>
                        <TableCell sx={{ width: '18%' }}>
                           <TextField
                             value={edit.panelLabor !== undefined ? edit.panelLabor : (panel ? panel.laborCost : '')}
                             onChange={e => handleConnectorEdit(connector.id, 'panelLabor', e.target.value)}
                             size="small"
                             type="number"
                             inputProps={{ 
                               min: 0, 
                               step: 0.01,
                               style: { 
                                 WebkitAppearance: 'none',
                                 MozAppearance: 'textfield'
                               }
                             }}
                             sx={{ 
                               width: '100%',
                               '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                 WebkitAppearance: 'none',
                                 margin: 0
                               },
                               '& input[type=number]': {
                                 MozAppearance: 'textfield'
                               }
                             }}
                           />
                         </TableCell>
                        <TableCell sx={{ width: '18%' }}>
                           <TextField
                             value={edit.inlinePart !== undefined ? edit.inlinePart : (inline ? inline.partCost : '')}
                             onChange={e => handleConnectorEdit(connector.id, 'inlinePart', e.target.value)}
                             size="small"
                             type="number"
                             inputProps={{ 
                               min: 0, 
                               step: 0.01,
                               style: { 
                                 WebkitAppearance: 'none',
                                 MozAppearance: 'textfield'
                               }
                             }}
                             sx={{ 
                               width: '100%',
                               '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                 WebkitAppearance: 'none',
                                 margin: 0
                               },
                               '& input[type=number]': {
                                 MozAppearance: 'textfield'
                               }
                             }}
                           />
                         </TableCell>
                        <TableCell sx={{ width: '18%' }}>
                           <TextField
                             value={edit.inlineLabor !== undefined ? edit.inlineLabor : (inline ? inline.laborCost : '')}
                             onChange={e => handleConnectorEdit(connector.id, 'inlineLabor', e.target.value)}
                             size="small"
                             type="number"
                             inputProps={{ 
                               min: 0, 
                               step: 0.01,
                               style: { 
                                 WebkitAppearance: 'none',
                                 MozAppearance: 'textfield'
                               }
                             }}
                             sx={{ 
                               width: '100%',
                               '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                 WebkitAppearance: 'none',
                                 margin: 0
                               },
                               '& input[type=number]': {
                                 MozAppearance: 'textfield'
                               }
                             }}
                           />
                         </TableCell>
                        <TableCell sx={{ width: '8%' }}>
                          {isDirty ? (
                            <Button
                              onClick={() => handleSaveConnector(connector.id)}
                              disabled={savingRows[connector.id]}
                              size="small"
                              variant="contained"
                            >
                              {savingRows[connector.id] ? <CircularProgress size={18} /> : 'Save'}
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {Object.keys(connectorEdits).length > 0 && (
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button
                  onClick={handleSaveAll}
                  disabled={saveAllLoading}
                  variant="contained"
                  color="primary"
                  startIcon={saveAllLoading ? <CircularProgress size={18} /> : null}
                >
                  Save All
                </Button>
              </Box>
            )}
          </Box>
        )}

        {activeTab === 5 && (
          <Box>
            <Typography variant="h6" gutterBottom>Real-time Updates</Typography>
            <div className="real-time-updates">
              {realTimeUpdates.length === 0 ? (
                <p>No recent updates</p>
              ) : (
                realTimeUpdates.map((update, index) => (
                  <div key={index} className="update-item">
                    <span className="update-type">{update.type}</span>
                    <span className="update-time">{formatTimestamp(update.timestamp)}</span>
                    {update.section && <span className="update-section">{update.section}</span>}
                  </div>
                ))
              )}
            </div>
          </Box>
        )}

        {activeTab === 6 && (
          <Box>
            <Typography variant="h6" gutterBottom>Send Notification</Typography>
            <div className="form-group">
              <label>Message:</label>
              <TextField
                value={notificationMessage}
                onChange={e => setNotificationMessage(e.target.value)}
                placeholder="Enter notification message..."
                fullWidth
                multiline
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Type:</label>
              <Select
                value={notificationType}
                onChange={e => setNotificationType(e.target.value)}
              >
                <MenuItem value="notification">Notification</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="success">Success</MenuItem>
              </Select>
            </div>
            <Button onClick={handleSendNotification} disabled={loading} variant="contained" sx={{ mt: 2 }}>
              {loading ? 'Sending...' : 'Send Notification'}
            </Button>
          </Box>
        )}

        {activeTab === 7 && (
          <Box>
            <Typography variant="h6" gutterBottom>Recent Changes (24h)</Typography>
            <div className="changes-list">
              {recentChanges.length === 0 ? (
                <p>No recent changes</p>
              ) : (
                recentChanges.map((change, index) => (
                  <div key={index} className="change-item">
                    <div className="change-header">
                      <span className="change-type">{change.type}</span>
                      <span className="change-time">{formatTimestamp(change.timestamp)}</span>
                    </div>
                    {change.section && <div className="change-section">{change.section}</div>}
                    {change.reason && <div className="change-reason">{change.reason}</div>}
                  </div>
                ))
              )}
            </div>
          </Box>
        )}
      </Box>

      <Dialog
        open={historyDialog}
        onClose={() => setHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Pricing History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Changes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* The pricingHistory state was removed, so this loop will not render anything */}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <BulkPriceUpdate
        open={bulkUpdateDialog}
        onClose={() => setBulkUpdateDialog(false)}
        onUpdate={handleBulkUpdate}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PricingAdmin; 